import React from 'react';
import { MoreOutlined } from '@ant-design/icons';

export interface HeaderMenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  /** Estilo destructivo (rojo) + separador arriba */
  danger?: boolean;
  disabled?: boolean;
  /** Ocultar el ítem sin sacarlo del arreglo */
  hidden?: boolean;
}

export interface HeaderActionMenuProps {
  items: HeaderMenuItem[];
  title?: string;
}

/**
 * Menú "···" de acciones secundarias para el header de detalle.
 * Mismo alto que HeaderActionButton (~40px) y alineado a la derecha
 * para no salirse de pantalla en mobile.
 */
const HeaderActionMenu: React.FC<HeaderActionMenuProps> = ({
  items,
  title = 'Más acciones',
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const visibleItems = items.filter((i) => !i.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title={title}
        aria-label={title}
      >
        <MoreOutlined className="text-lg" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-dark-bg rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          {visibleItems.map((item, index) => (
            <React.Fragment key={item.key}>
              {item.danger && index > 0 && (
                <hr className="my-1 border-gray-200 dark:border-gray-700" />
              )}
              <button
                type="button"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  setOpen(false);
                  item.onClick();
                }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  item.danger
                    ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeaderActionMenu;
